// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";
import { AnnouncementBannerFields } from "@store/Library/Components/GreifCarousel/types";

const enum fields {
    badgeText = "badgeText",
    badgeShowDot = "badgeShowDot",
    badgeDotColor = "badgeDotColor",
    badgeTextColor = "badgeTextColor",
    badgeBackgroundColor = "badgeBackgroundColor",
    heading = "heading",
    bodyText = "bodyText",
}

export interface AnnouncementBannerWidgetPresentationProps extends ExpandStyles<AnnouncementBannerWidgetStyles> {}

interface AnnouncementBannerWidgetProps extends AnnouncementBannerWidgetPresentationProps, WidgetProps {
    fields: AnnouncementBannerFields;
}

export interface AnnouncementBannerWidgetStyles {
    bannerWrapper?: InjectableCss;
    badgePill?: InjectableCss;
    badgeDot?: InjectableCss;
    badgeText?: InjectableCss;
    headingWrapper?: InjectableCss;
    bodyWrapper?: InjectableCss;
}

export const announcementBannerWidgetStyles: AnnouncementBannerWidgetStyles = {
    bannerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: ${pxToRem(8)};
            flex: 1;
            min-width: 0;
            align-self: stretch;
        `,
    },
    badgePill: {
        css: css`
            display: inline-flex;
            flex-direction: row;
            align-items: center;
            padding: ${pxToRem(4)} ${pxToRem(8)};
            gap: ${pxToRem(4)};
            border-radius: ${pxToRem(100)};
        `,
    },
    badgeDot: {
        css: css`
            width: ${pxToRem(4)};
            height: ${pxToRem(4)};
            border-radius: 50%;
            flex-shrink: 0;
        `,
    },
    badgeText: {
        css: css`
            font-size: ${pxToRem(12)};
            font-weight: ${x => x.theme.design.typography.weight.base};
            line-height: 1.2;
            text-transform: uppercase;
        `,
    },
    headingWrapper: {
        css: css`
            align-self: stretch;
            font-size: ${pxToRem(24)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: 1.2;
            color: ${getColor("primary.contrast")};
        `,
    },
    bodyWrapper: {
        css: css`
            align-self: stretch;
            font-size: ${x => x.theme.design.typography.size.base};
            line-height: 1.4;
            color: ${getColor("text.lightest")};
        `,
    },
};

const AnnouncementBannerWidget: FC<AnnouncementBannerWidgetProps> = ({ extendedStyles, fields, overridedStyles }) => {
    const styles = useExpandProps(announcementBannerWidgetStyles, extendedStyles, overridedStyles);
    const badgeText = fields.badgeText?.trim();

    return (
        <StyledWrapper {...styles.bannerWrapper}>
            {Boolean(badgeText) && (
                <StyledWrapper {...styles.badgePill} style={{ background: fields.badgeBackgroundColor }}>
                    {fields.badgeShowDot && (
                        <StyledWrapper {...styles.badgeDot} style={{ backgroundColor: fields.badgeDotColor }} />
                    )}
                    <StyledWrapper {...styles.badgeText} as="span" style={{ color: fields.badgeTextColor }}>
                        {badgeText}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.headingWrapper} as="div">
                {parse(fields.heading || "", parserOptions)}
            </StyledWrapper>
            <StyledWrapper {...styles.bodyWrapper} as="div">
                {parse(fields.bodyText || "", parserOptions)}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: AnnouncementBannerWidget,
    definition: {
        displayName: "Announcement Banner",
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.badgeText,
                displayName: "Badge Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tooltip: "Small pill label shown above the heading. Leave empty to hide the badge.",
            },
            {
                name: fields.badgeShowDot,
                displayName: "Show Badge Dot",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                isVisible: (item: any) => Boolean(item.fields[fields.badgeText]),
            },
            {
                name: fields.badgeDotColor,
                displayName: "Badge Dot Color",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "#F28900",
                fieldType: "Contextual",
                isVisible: (item: any) => Boolean(item.fields[fields.badgeText]) && item.fields[fields.badgeShowDot],
            },
            {
                name: fields.badgeTextColor,
                displayName: "Badge Text Color",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "#FFFFFF",
                fieldType: "Contextual",
                isVisible: (item: any) => Boolean(item.fields[fields.badgeText]),
            },
            {
                name: fields.badgeBackgroundColor,
                displayName: "Badge Background Color",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "#F5F5F526",
                fieldType: "Contextual",
                isVisible: (item: any) => Boolean(item.fields[fields.badgeText]),
            },
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.bodyText,
                displayName: "Body Text",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
